import React from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '../../components/ui/sidebar';
import { NavGroup } from '../../components/layout/nav-group';
import { NavUser, NavUserProps } from '../../components/layout/nav-user';
import { TeamSwitcher, TeamSwitcherProps } from '../../components/layout/team-switcher';
import { NavLink } from '../../components/layout/types'; // Import correct Nav types
import { useTranslation } from 'react-i18next'; // Added i18next import

import { useAuthWithCache } from '@/hooks/use-auth-with-cache';
import { Users, Building, FileText, Shield, Globe, Activity, Languages, AlertCircle, MessageSquare, BarChart3 } from 'lucide-react';
import { useIsClientCompany } from '@/hooks/use-is-client-company';
import { useCurrentUserProfile } from '@/features/users/hooks/useCurrentUserProfile';
import { componentLogger } from '@/lib/logging';
import { invokeEdgeFunction } from '@/lib/supabase-functions-wrapper';

// Define Profile and Company types based on schema
type Profile = {
  id: string;
  first_name?: string;
  last_name?: string;
  app_role?: 'ADMIN' | 'USER' | 'COMPANY_ADMIN'; 
  is_representative?: boolean;
  company_id?: string;
  email?: string;
};

type Company = {
  id: string;
  name?: string;
  type?: 'CLIENT' | 'FACILITATOR' | 'WORKSHOP';
  logo_attachment_id?: string | null;
};

// Simplified and corrected formatCompanyType
const formatCompanyType = (typeValue: string | undefined, t: Function) => { 
  if (!typeValue) return t('common.notAvailableShort');

  const typeKeyForT = `enums.companyType.${typeValue.toLowerCase()}`;
  // Provide a fallback that is noticeably different if translation is missing
  const fallbackText = `${typeValue} (translation missing)`; 
  return t(typeKeyForT, fallbackText);
};

const formatUserRole = (app_role?: 'ADMIN' | 'USER' | 'COMPANY_ADMIN' | string) => {
  if (!app_role) return '';
  switch (app_role) {
    case 'ADMIN': return 'System Admin';
    case 'USER': return 'User';
    case 'COMPANY_ADMIN': return 'Company Admin';
    default: return app_role;
  }
};


export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { user, loading: isLoadingUser, signOut } = useAuthWithCache();
  const { profile: userProfile, loading: isLoadingProfile } = useCurrentUserProfile();
  const { isClientCompany } = useIsClientCompany();

  // Transform UserProfile to local Profile type (for backward compatibility)
  const profile: Profile | null = userProfile ? ({
    id: userProfile.id,
    first_name: userProfile.first_name ?? undefined,
    last_name: userProfile.last_name ?? undefined,
    app_role: userProfile.app_role as Profile['app_role'],
    is_representative: userProfile.is_representative ?? undefined,
    company_id: userProfile.company_id ?? undefined,
    email: userProfile.email ?? undefined,
  } as Profile) : null;

  // Get company data from profile (already included in hook response)
  const company: Company | null = userProfile?.company ? ({
    id: userProfile.company.id,
    name: userProfile.company.name,
    type: userProfile.company.type,
    logo_attachment_id: userProfile.company.logo_attachment_id,
  } as Company) : null;

  const isLoading = isLoadingUser || isLoadingProfile;

  // Fetch company logo URL from attachment ID
  const [companyLogoUrl, setCompanyLogoUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchLogoUrl = async () => {
      if (!company?.logo_attachment_id) {
        setCompanyLogoUrl(null);
        return;
      }

      try {
        componentLogger.debug('app-sidebar', 'Fetching company logo URL', {
          attachmentId: company.logo_attachment_id,
        });

        const { data, error } = await invokeEdgeFunction<{ presignedUrl: string }>(
          'get-download-url-for-attachment',
          {
            body: { attachmentId: company.logo_attachment_id }
          }
        );

        if (error) {
          componentLogger.error('app-sidebar', 'Failed to fetch company logo URL', { error });
          setCompanyLogoUrl(null);
          return;
        }

        if (data?.presignedUrl) {
          setCompanyLogoUrl(data.presignedUrl);
          componentLogger.debug('app-sidebar', 'Company logo URL fetched successfully');
        } else {
          setCompanyLogoUrl(null);
        }
      } catch (error) {
        componentLogger.error('app-sidebar', 'Error fetching company logo URL', { error });
        setCompanyLogoUrl(null);
      }
    };

    fetchLogoUrl();
  }, [company?.logo_attachment_id]);

  // Following UI Security Patterns: Check for company visibility issues
  const hasCompanyVisibilityIssue = () => {
    // If profile is loaded but has no company_id when it should
    return profile && !isLoading && !profile.company_id && profile.app_role !== 'ADMIN';
  };

  // Only show profile data if it matches the authenticated user
  const navUserProps: NavUserProps = {
    isLoading: isLoading,
    ...(profile && profile.id === user?.id && profile.first_name && { firstName: profile.first_name }),
    ...(profile && profile.id === user?.id && profile.last_name && { lastName: profile.last_name }),
    ...(profile && profile.id === user?.id && { role: formatUserRole(profile.app_role) }),
    ...(profile && profile.id === user?.id && profile.is_representative !== undefined && { isRepresentative: profile.is_representative }),
    ...(signOut && { onSignOut: signOut }),
    ...(user?.id && { userId: user.id }),
    ...(userProfile && userProfile.id === user?.id && { userProfile }),
  };
  
  const allNavItems: NavLink[] = [
    { title: t('nav.projects'), icon: FileText, url: '/projects', 'data-testid': 'nav-projects' },
    { title: t('nav.companies'), icon: Building, url: '/companies', 'data-testid': 'nav-companies' },
    { title: t('nav.users'), icon: Users, url: '/users', 'data-testid': 'nav-users' },
  ];

  // Add Admin menu items for System Admin only
  if (profile?.app_role === 'ADMIN') {
    allNavItems.push({
      title: t('nav.adminPermissions'),
      icon: Shield,
      url: '/admin/permissions',
      'data-testid': 'nav-admin-permissions'
    });
    allNavItems.push({
      title: t('nav.adminLanguages'),
      icon: Globe,
      url: '/admin/languages',
      'data-testid': 'nav-admin-languages'
    });
    allNavItems.push({
      title: t('nav.adminTranslations'),
      icon: Languages,
      url: '/admin/translations',
      'data-testid': 'nav-admin-translations'
    });
    allNavItems.push({
      title: t('nav.adminPerformance'),
      icon: Activity,
      url: '/admin/performance',
      'data-testid': 'nav-admin-performance'
    });
    allNavItems.push({
      title: t('nav.adminErrorLogs'),
      icon: AlertCircle,
      url: '/admin/error-logs',
      'data-testid': 'nav-admin-error-logs'
    });
    allNavItems.push({
      title: t('nav.adminTranslationMonitoring'),
      icon: MessageSquare,
      url: '/admin/translation-monitoring',
      'data-testid': 'nav-admin-translation-monitoring'
    });
    allNavItems.push({
      title: t('nav.adminMailStats'),
      icon: BarChart3,
      url: '/admin/mail-stats',
      'data-testid': 'nav-admin-mail-stats'
    });
  }

  // Filter out subprojects menu item for CLIENT company users
  let generalNavItems = isClientCompany 
    ? allNavItems.filter(item => item.url !== '/subprojects')
    : allNavItems;

  // Filter out companies and users menu items for regular Users
  if (profile?.app_role === 'USER') {
    generalNavItems = generalNavItems.filter(
      item => item.url !== '/companies' && item.url !== '/users'
    );
  }



  const teamSwitcherProps: TeamSwitcherProps = {
    isLoading: isLoading,
    currentTeam: profile?.app_role === 'ADMIN' ? {
      // System Admin special case - they have no company by design
      id: 'system-admin',
      name: t('common.systemAdmin'),
      subtext: t('common.allCompanies'),
    } : company ? {
      id: company.id,
      name: company.name || t('common.noCompanyName'),
      subtext: formatCompanyType(company.type, t),
      ...(companyLogoUrl && { logoUrl: companyLogoUrl }),
    } : (hasCompanyVisibilityIssue() ? {
      id: 'loading',
      name: t('common.loadingCompany'),
      subtext: t('common.pleaseWait'),
    } : null),
    teams: profile?.app_role === 'ADMIN' ? [{
      // System Admin sees all companies
      id: 'system-admin',
      name: t('common.systemAdmin'),
      subtext: t('common.allCompanies'),
    }] : company ? [{
      id: company.id,
      name: company.name || t('common.noCompanyName'),
      subtext: formatCompanyType(company.type, t),
      ...(companyLogoUrl && { logoUrl: companyLogoUrl }),
    }] : [],
    onTeamSwitch: (teamId: string) => {
      // Log team switch attempts for debugging
      componentLogger.warn('Team switch attempt to:', teamId);
    },
  };

  return (
    <Sidebar collapsible='icon' variant='floating' data-testid='app-sidebar' {...props}>
      <SidebarHeader data-testid='sidebar-header'>
        <TeamSwitcher data-testid='team-switcher' {...teamSwitcherProps} />
      </SidebarHeader>
      <SidebarContent data-testid='sidebar-content'>
        <NavGroup data-testid='nav-group-general' title={t('nav.group.general')} items={generalNavItems} />
      </SidebarContent>
      <SidebarFooter data-testid='sidebar-footer'>
        <NavUser data-testid='nav-user-profile' {...navUserProps} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
